import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/confirm/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(res.data);
          return true;
        } else if (res.data.payment_status === 'failed' || res.data.status === 'expired') {
          setStatus('error');
          return true;
        }
      } catch {
        // continue polling
      }
      return false;
    };

    const poll = async () => {
      const done = await checkStatus();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, 2000);
      } else if (!done) {
        setStatus('timeout');
      }
    };

    poll();
  }, [sessionId]);

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase()
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="text-center max-w-sm">
        {status === 'checking' && (
          <div className="animate-fade-in-up" data-testid="checking-status">
            <Loader2 className="w-16 h-16 text-orange-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Processing Payment
            </h1>
            <p className="text-slate-500">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-scale-in" data-testid="success-status">
            <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Payment Successful
            </h1>
            {paymentData && (
              <p className="text-3xl font-extrabold my-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {formatCurrency(paymentData.amount, paymentData.currency)}
              </p>
            )}
            <p className="text-slate-500 text-sm">Thank you! You can safely close this page.</p>
          </div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <div className="animate-scale-in" data-testid="error-status">
            <XCircle className="w-20 h-20 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {status === 'timeout' ? 'Status Unknown' : 'Payment Issue'}
            </h1>
            <p className="text-slate-500 text-sm">
              {status === 'timeout'
                ? 'We could not confirm the payment status. Please check with the merchant.'
                : 'There was an issue with this payment. Please contact the merchant.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
